import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, fileUrl } from "../lib/api";
import i18n, { initI18n, changeLanguage } from "../lib/i18n";

const SiteContext = createContext(null);

function prefersReduced() {
  const stored = localStorage.getItem("reduce-motion");
  if (stored !== null) return stored === "1";
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false;
}

function applyHead(settings, lng) {
  const title = settings.seo_title?.[lng] || settings.seo_title?.en || i18n.t("brand.name");
  document.title = title;
  let meta = document.querySelector('meta[name="description"]');
  if (meta) meta.setAttribute("content", settings.seo_description?.[lng] || settings.seo_description?.en || "");
  if (settings.favicon_image_id) {
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = fileUrl(settings.favicon_image_id);
  }
}

export function SiteProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [lang, setLang] = useState("en");
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reducedMotion, setReducedMotionState] = useState(prefersReduced());

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api.get("/site"), api.get("/i18n/languages")]);
      setSettings(s.data);
      setLanguages(l.data.languages);
      const lng = await initI18n(l.data.languages, l.data.default);
      setLang(lng);
      applyHead(s.data, lng);
      setReady(true);
    } catch (e) {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    document.documentElement.classList.toggle("reduce-motion", reducedMotion);
  }, [reducedMotion]);

  const setLanguage = async (code) => {
    await changeLanguage(code);
    setLang(code);
    if (settings) applyHead(settings, code);
  };

  const setReducedMotion = (v) => {
    localStorage.setItem("reduce-motion", v ? "1" : "0");
    setReducedMotionState(v);
  };

  const refreshSettings = async () => {
    const s = await api.get("/site");
    setSettings(s.data);
    applyHead(s.data, lang);
  };

  const value = { settings, languages, lang, setLanguage, reducedMotion, setReducedMotion, refreshSettings, ready };
  if (failed) {
    return (
      <div className="min-h-screen grid place-items-center p-8 text-center" data-testid="boot-error">
        <div>
          <p className="font-heading text-2xl mb-4">Connection problem · Problema di connessione</p>
          <button className="btn-primary" onClick={() => window.location.reload()}>Retry · Riprova</button>
        </div>
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))]" data-testid="boot-loading">
        <div className="boot-glow" aria-hidden="true" />
      </div>
    );
  }
  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
}

export const useSite = () => useContext(SiteContext);
